import React from 'react';
import { MMMConfigFormState } from '../../types/mmmConfig';

interface SmartColumnMappingSectionProps {
    config: MMMConfigFormState;
    dataColumns: string[];
    onChange: (config: Partial<MMMConfigFormState>) => void;
}

type ColumnRole = 'media_spend' | 'control' | 'ignore'; // | 'media_exposure' (future)

export const SmartColumnMappingSection: React.FC<SmartColumnMappingSectionProps> = ({
    config,
    dataColumns,
    onChange,
}) => {
    // We need to derive the current state of mapping from the config
    // to ensure we display the correct radio button selection.
    const getColumnRole = (col: string): ColumnRole => {
        // Check if it's dependent variable or date variable (exclude from mapping)
        if (col === config.dep_var || col === config.date_var) {
            return 'ignore';
        }

        // Check media channels
        const isMedia = config.media_channels?.some((c) => c.column === col);
        if (isMedia) return 'media_spend';

        // Check control vars
        const isControl = config.control_vars?.some((c) => c.column === col);
        if (isControl) return 'control';

        return 'ignore';
    };

    const handleRoleChange = (col: string, newRole: ColumnRole) => {
        // 1. Remove from existing lists first (clean slate for this column)
        const newMediaChannels = (config.media_channels || []).filter((c) => c.column !== col);
        const newControlVars = (config.control_vars || []).filter((c) => c.column !== col);

        // 2. Add to new list based on role
        if (newRole === 'media_spend') {
            newMediaChannels.push({
                name: col, // Default name = column name
                column: col,
                variable_column: null,
                adstock: 'geometric',
                saturation: 'hill',
            });
        } else if (newRole === 'control') {
            newControlVars.push({
                name: col, // Default name = column name
                column: col,
            });
        }

        // 3. Update config
        onChange({
            media_channels: newMediaChannels,
            control_vars: newControlVars,
        });
    };

    return (
        <div className="card">
            <h2 className="text-xl font-bold mb-4 text-slate-800 tracking-tight">Smart Column Mapping</h2>
            <p className="text-sm text-slate-600 mb-6">
                Map your data columns to model inputs. Select <strong className="text-slate-800">Media (Spend)</strong> for marketing channels you want to optimize,
                and <strong className="text-slate-800">Control</strong> for external factors (like price, holidays, competitor activity).
            </p>

            <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-gradient-to-r from-slate-50 to-slate-100/80">
                        <tr>
                            <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                                Column Name
                            </th>
                            <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                                Role
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-100">
                        {dataColumns
                            .filter((col) => col !== config.dep_var && col !== config.date_var) // specific filter to hide dep/date vars
                            .map((col, index) => {
                                const currentRole = getColumnRole(col);
                                return (
                                    <tr key={col} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-800">
                                            {col}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                                            <div className="flex items-center space-x-5">
                                                <label className="inline-flex items-center cursor-pointer group">
                                                    <input
                                                        type="radio"
                                                        className="w-4 h-4 text-indigo-600 border-2 border-slate-300 focus:ring-2 focus:ring-indigo-500/20 transition-colors"
                                                        name={`role-${col}`}
                                                        value="media_spend"
                                                        checked={currentRole === 'media_spend'}
                                                        onChange={() => handleRoleChange(col, 'media_spend')}
                                                    />
                                                    <span className={`ml-2 font-medium transition-colors ${currentRole === 'media_spend' ? 'text-indigo-700' : 'text-slate-600 group-hover:text-slate-800'}`}>
                                                        Media (Spend)
                                                    </span>
                                                </label>

                                                <label className="inline-flex items-center cursor-pointer group">
                                                    <input
                                                        type="radio"
                                                        className="w-4 h-4 text-emerald-600 border-2 border-slate-300 focus:ring-2 focus:ring-emerald-500/20 transition-colors"
                                                        name={`role-${col}`}
                                                        value="control"
                                                        checked={currentRole === 'control'}
                                                        onChange={() => handleRoleChange(col, 'control')}
                                                    />
                                                    <span className={`ml-2 font-medium transition-colors ${currentRole === 'control' ? 'text-emerald-700' : 'text-slate-600 group-hover:text-slate-800'}`}>
                                                        Control
                                                    </span>
                                                </label>

                                                <label className="inline-flex items-center cursor-pointer group">
                                                    <input
                                                        type="radio"
                                                        className="w-4 h-4 text-slate-400 border-2 border-slate-300 focus:ring-2 focus:ring-slate-400/20 transition-colors"
                                                        name={`role-${col}`}
                                                        value="ignore"
                                                        checked={currentRole === 'ignore'}
                                                        onChange={() => handleRoleChange(col, 'ignore')}
                                                    />
                                                    <span className={`ml-2 font-medium transition-colors ${currentRole === 'ignore' ? 'text-slate-500' : 'text-slate-600 group-hover:text-slate-800'}`}>
                                                        Ignore
                                                    </span>
                                                </label>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                    </tbody>
                </table>
            </div>

            {dataColumns.length === 0 && (
                <div className="text-center py-10 text-slate-500">
                    <p className="font-medium">No columns available.</p>
                    <p className="text-sm mt-1">Please upload a dataset first.</p>
                </div>
            )}
        </div>
    );
};
